/**
 * Custom concept task/subtask modal.
 *
 * Tasks flagged `from_custom_concept` on the task overview get a different
 * modal (fullscreen, Brief tab, parameter-tagged threads).
 */

import _ from 'lodash';
import {
  templates_formats,
  templates_sizes,
  toFormatLabel,
} from 'pages/Brief/constant';

// Overview keys dropped from the custom concept Overview tab. `channel` comes
// back as a list there, so the channel row reads `platforms` instead.
export const CUSTOM_CONCEPT_HIDDEN_OVERVIEW_KEYS = [
  'channel',
  'refresh',
  'triggers',
  'desktop_displays',
  'mobile_displays',
  'platform_link',
];

// Panels under the Overview tab dropped for custom concept tasks
export const CUSTOM_CONCEPT_HIDDEN_PANEL_KEYS = ['templates'];

// Task overview fields only custom concept tasks carry, shown after the
// partner group.
export const CUSTOM_CONCEPT_OVERVIEW_FIELDS = [
  { key: 'platforms', name: 'Channels', tooltip: null },
];

/**
 * Thread tagging groups. Their options are read from the task's brief, see
 * `getDeliverableTree` and `getThreadParameterOptions`.
 */
export const CUSTOM_THREAD_PARAMETERS = [
  {
    key: 'channels',
    name: 'Channels',
    guide: 'Channels this thread applies to.',
  },
  {
    key: 'formats',
    name: 'Formats',
    guide: 'Creative formats this thread applies to.',
  },
  {
    key: 'sizes',
    name: 'Sizes',
    guide: 'Template sizes this thread applies to.',
  },
  {
    key: 'languages',
    name: 'Languages',
    guide: 'Languages this thread applies to.',
  },
  {
    key: 'markets',
    name: 'Markets',
    guide: 'Markets this thread applies to.',
  },
];

export const EMPTY_THREAD_PARAMETERS = {
  channels: [],
  formats: [],
  sizes: [],
  languages: [],
  markets: [],
};

const PARAMETER_KEYS = CUSTOM_THREAD_PARAMETERS.map((group) => group.key);

const toValueList = (value) => {
  if (_.isArray(value))
    return _.compact(
      value.map((item) =>
        _.isPlainObject(item) ? item.value ?? item.name : item
      )
    );

  if (_.isString(value)) return _.compact(value.split(',').map(_.trim));

  return [];
};

/**
 * Language and market options, read from a brief shaped like the
 * `admin/adweave-briefs` response. Channels, formats and sizes come from
 * `getDeliverableTree` instead.
 */
export const getThreadParameterOptions = (briefOverview) => ({
  languages: _.uniq(toValueList(briefOverview?.languages)),
  markets: _.uniq(toValueList(briefOverview?.markets)),
});

/**
 * Channels, formats and sizes nest in the brief (a size belongs to one format
 * on one channel), so they are picked from one tree and saved with their
 * parents in the value, e.g. `Yahoo DSP – Display (H5) – 120x600`.
 */
export const DELIVERABLE_KEYS = ['channels', 'formats', 'sizes'];

// Values are saved and filtered comma separated, so a label cannot carry one
const toTagLabel = (value) =>
  String((_.isPlainObject(value) ? value.value ?? value.name : value) ?? '')
    .replace(/\s*,\s*/g, ' / ')
    .trim();

const toDurations = (duration) =>
  _.compact(
    _.flatMap(_.castArray(duration ?? []), (item) =>
      String(_.isPlainObject(item) ? item.value ?? '' : item).split(',')
    ).map(_.trim)
  );

const toNode = (id, label, key, children = []) => ({
  id,
  label,
  key,
  children,
});

const toSizeNodes = (formatId, sizes) =>
  (Array.isArray(sizes) ? sizes : [])
    .map((size) => {
      const label = toTagLabel(size);
      const id = `${formatId} – ${label}`;

      return toNode(
        id,
        label,
        'sizes',
        toDurations(size?.duration).map((duration) =>
          toNode(`${id} – ${duration}`, duration, 'sizes')
        )
      );
    })
    .filter((node) => node.label);

// scratch_ and reuse_ formats of a platform land on the same channel
const mergeNode = (siblings, node) => {
  const existing = _.find(siblings, { id: node.id });

  if (!existing) {
    siblings.push(node);
    return;
  }

  node.children.forEach((child) => mergeNode(existing.children, child));
};

// e.g. `scratch_meta_static_template_sizes` sits under
// `scratch_meta_template_formats`
const findFormatsEntry = (sizesKey) =>
  _.find(templates_formats, ({ key }) =>
    sizesKey.startsWith(`${key.replace(/_template_formats$/, '')}_`)
  );

/**
 * Channel > format > size > duration nodes of the brief, shaped
 * `{ id, label, key, children }`. `id` is the value saved under `key`.
 */
export const getDeliverableTree = (briefOverview) => {
  const brief = briefOverview?.brief ?? {};
  const tree = [];

  const getChannel = (name) => {
    const id = toTagLabel(name);
    let channel = _.find(tree, { id });

    if (!channel) {
      channel = toNode(id, id, 'channels');
      tree.push(channel);
    }

    return channel;
  };

  templates_formats.forEach(({ key, name }) => {
    const formats = Array.isArray(brief[key]) ? brief[key] : [];

    if (_.isEmpty(formats)) return;

    const channel = getChannel(name);

    formats.forEach((format) => {
      const label = toTagLabel(format);

      if (!label) return;

      const id = toFormatLabel(channel.id, label);

      mergeNode(
        channel.children,
        toNode(id, label, 'formats', toSizeNodes(id, format?.selections))
      );
    });
  });

  // Pre V2 briefs keep sizes in their own `_template_sizes` keys
  templates_sizes.forEach(({ key, name }) => {
    if (!Array.isArray(brief[key]) || _.isEmpty(brief[key])) return;

    const channel = getChannel(findFormatsEntry(key)?.name ?? 'Other');
    const id = toTagLabel(name);

    mergeNode(
      channel.children,
      toNode(id, id, 'formats', toSizeNodes(id, brief[key]))
    );
  });

  if (_.isEmpty(tree))
    _.uniq([
      ...toValueList(brief.scratch_platforms_advertising_on),
      ...toValueList(brief.reuse_platforms_advertising_on),
    ]).forEach(getChannel);

  return tree;
};

const getLeaves = (node) =>
  _.isEmpty(node.children) ? [node] : _.flatMap(node.children, getLeaves);

/** Ids of the leaves under the given node(s). */
export const getLeafIds = (nodes) =>
  _.flatMap(_.castArray(nodes), getLeaves).map((leaf) => leaf.id);

/**
 * Channels, formats and sizes for the ticked leaves. Each leaf brings its
 * parents, so filtering at any level still finds it.
 */
export const toDeliverableParams = (tickedLeafIds, tree) => {
  const ticked = new Set(tickedLeafIds);
  const params = { channels: [], formats: [], sizes: [] };

  const walk = (node, ancestors) => {
    const path = [...ancestors, node];

    if (_.isEmpty(node.children)) {
      if (ticked.has(node.id))
        path.forEach((item) => params[item.key].push(item.id));
      return;
    }

    node.children.forEach((child) => walk(child, path));
  };

  tree.forEach((node) => walk(node, []));

  return _.mapValues(params, _.uniq);
};

/** Leaves of the tree the saved params tick. */
export const getTickedLeaves = (params, tree) =>
  _.flatMap(tree, getLeaves)
    .filter((leaf) => (params?.[leaf.key] ?? []).includes(leaf.id))
    .map((leaf) => leaf.id);

/** The tree cut down to the leaves `allowed` holds, e.g. a reply's thread. */
export const narrowDeliverableTree = (tree, allowed) =>
  tree.flatMap((node) => {
    if (_.isEmpty(node.children))
      return (allowed?.[node.key] ?? []).includes(node.id) ? [node] : [];

    const children = narrowDeliverableTree(node.children, allowed);

    return _.isEmpty(children) ? [] : [{ ...node, children }];
  });

const getAllIds = (node) => [node.id, ..._.flatMap(node.children, getAllIds)];

/**
 * Saved deliverables for display: one row per tagged format with its sizes
 * (durations as `size – duration`), and whatever the tree does not cover,
 * like channels without formats or values tagged before the tree.
 */
export const groupDeliverables = (params, tree) => {
  const has = (key, id) => (params?.[key] ?? []).includes(id);
  const covered = new Set();
  const rows = [];

  tree.forEach((channel) =>
    channel.children.forEach((format) => {
      if (!has('formats', format.id)) return;

      const sizes = _.flatMap(format.children, (size) =>
        _.isEmpty(size.children)
          ? [{ id: size.id, label: size.label }]
          : size.children.map((duration) => ({
              id: duration.id,
              label: `${size.label} – ${duration.label}`,
            }))
      ).filter((size) => has('sizes', size.id));

      rows.push({ id: format.id, sizes: sizes.map((size) => size.label) });
      covered.add(channel.id);
      getAllIds(format).forEach((id) => covered.add(id));
    })
  );

  return {
    rows,
    rest: _.fromPairs(
      DELIVERABLE_KEYS.map((key) => [
        key,
        (params?.[key] ?? []).filter((value) => !covered.has(value)),
      ])
    ),
  };
};

/** Size values without the sizes that only group durations. */
export const getLeafSizes = (sizes, tree) => {
  const leafIds = new Set(getLeafIds(tree));
  const treeIds = new Set(_.flatMap(tree, getAllIds));

  return (sizes ?? []).filter((id) => leafIds.has(id) || !treeIds.has(id));
};

/**
 * `parameter_settings` comes back from the API either as an object keyed by
 * parameter, or as rows carrying the parameter name and its value(s).
 */
export const parseParameterSettings = (settings) => {
  const parsed = { ...EMPTY_THREAD_PARAMETERS };

  if (_.isEmpty(settings)) return parsed;

  const readRow = (row) => {
    if (!_.isPlainObject(row)) return;

    const type = _.toLower(row.type ?? row.name ?? row.key ?? row.parameter);

    if (PARAMETER_KEYS.includes(type)) {
      parsed[type] = _.union(
        parsed[type],
        toValueList(row.values ?? row.value)
      );
      return;
    }

    // A row can also be a plain object holding every parameter at once
    PARAMETER_KEYS.forEach((key) => {
      if (_.has(row, key))
        parsed[key] = _.union(parsed[key], toValueList(row[key]));
    });
  };

  _.isArray(settings) ? settings.forEach(readRow) : readRow(settings);

  return parsed;
};

/** Flattens the picker selection into the comma separated payload the API takes. */
export const serializeParameterSettings = (params) =>
  _.fromPairs(
    PARAMETER_KEYS.map((key) => [key, (params?.[key] ?? []).join(', ')])
  );

export const hasParameterFilters = (filters) =>
  _.some(filters, (values) => !_.isEmpty(values));
export const THREAD_STATUS_NOT_SET = 0;

// `not set` is the default, shown as a "Set status" prompt.
export const THREAD_STATUS_OPTIONS = [
  {
    value: THREAD_STATUS_NOT_SET,
    key: 'not_set',
    name: 'Not Set',
    color: '#9e9e9e',
  },
  { value: 1, key: 'not_started', name: 'Not Started', color: '#6e6e6e' },
  { value: 19, key: 'in_progress', name: 'In Progress', color: '#ffb648' },
  { value: 12, key: 'completed', name: 'Completed', color: '#1abc00' },
];

export const QA_TEAM_ID = 5;

// QA results set from a thread's status menu. They open the QA result dialog
// instead of saving a `thread_status`, and show the thread's `status.status`.
export const THREAD_QA_RESULT_OPTIONS = [
  {
    value: 'thread_resolve',
    key: 'resolved',
    name: 'Resolved',
    menuName: 'Resolve',
    color: '#1abc00',
    isQaResult: true,
  },
  {
    value: 'thread_reject',
    key: 'rejected',
    name: 'Rejected',
    menuName: 'Reject',
    color: '#ff4a4a',
    isQaResult: true,
  },
];

/**
 * Builds `thread_status` in the shape the threads response uses, where not set
 * comes back with a null id.
 */
export const toThreadStatus = (value) => ({
  id: value === THREAD_STATUS_NOT_SET ? null : String(value),
  name: _.find(THREAD_STATUS_OPTIONS, { value })?.key ?? null,
});
